import { MaintenanceWindow } from "@redux/slices/maintenanceSlice";
import { FailoverTableRow } from "kontrolEdge/views/OverviewComponents/Types";
import { formatDuration } from "kontrolEdge/helpers/formatDuration";

/**
 * What the Customer column shows for a platform window.
 *
 * Every other row in this table belongs to one customer, because it records
 * maintenance performed on that customer's own services. A platform window
 * belongs to none of them and applies to all of them at once, so naming a
 * customer would be wrong and leaving it blank would read as missing data.
 */
export const PLATFORM_MAINTENANCE_CUSTOMER = "All customers";

/** Local `HH:MM`, or "" when the timestamp does not parse. */
function timeOf(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/**
 * The description, which carries the whole meaning of the row.
 *
 * An operator's own message wins outright — if someone took the trouble to
 * write what this window is about, that is more useful than anything
 * generated here. The default does not count: it is three sentences written
 * for a banner and it would swamp the cell, which is why the API reports
 * `customMessage` as null when nobody authored one.
 *
 * Failing that, two things the generated sentence has to get right, both of
 * them the reason this text is pinned down in a tested helper rather than
 * typed inline in the view:
 *
 * - **What is under maintenance.** A reader scanning a table called
 *   "Maintenance" on a network dashboard assumes a date and a duration mean
 *   their circuit was touched. Here it is the monitoring platform, and their
 *   traffic is untouched — so that is said first, because it is the
 *   misreading that costs a support call.
 * - **Whether it has happened yet.** A scheduled window and one in progress
 *   are different messages. "will not be affected" is a promise about
 *   tonight; "are not affected" is a statement about right now. Using the
 *   wrong tense makes the notice read as an incident report.
 */
export function describeWindow(
  window: MaintenanceWindow,
  scheduled: boolean
): string {
  if (window.customMessage) return window.customMessage;

  const from = timeOf(window.since);
  const to = timeOf(window.until);
  const when = from && to ? `, ${from} – ${to}` : "";

  return scheduled
    ? `Scheduled maintenance on the monitoring services${when}. Your services will not be affected.`
    : `Maintenance in progress on the monitoring services${when}. Your services are not affected.`;
}

/**
 * The platform window as a row of the overview's Maintenance table, or null
 * when there is nothing to announce.
 *
 * Takes both the open window and the announced one because the announcement
 * is the point. In `blocked` mode the telemetry views are replaced while the
 * window runs, so a row that only appears once maintenance starts is a row
 * the customer never sees — the notice has to be on the overview *before*
 * the dashboard goes dark. An open window still wins over a scheduled one:
 * what is happening now is more useful than what is coming.
 */
export function buildPlatformMaintenanceRow(
  current: MaintenanceWindow | null | undefined,
  upcoming?: MaintenanceWindow | null
): FailoverTableRow | null {
  const window = current ?? upcoming ?? null;
  if (!window) return null;

  const scheduled = !current;
  const since = new Date(window.since);
  const until = new Date(window.until);
  const hasSpan =
    !Number.isNaN(since.getTime()) && !Number.isNaN(until.getTime());

  return {
    date: Number.isNaN(since.getTime()) ? "–" : since.toLocaleDateString(),
    description: describeWindow(window, scheduled),
    customer: PLATFORM_MAINTENANCE_CUSTOMER,
    // The announced span, not the elapsed time: what a reader needs from a
    // window — scheduled or running — is how long it lasts. A window whose
    // dates do not parse has no span to announce, and formatDuration renders
    // that as "on-going".
    duration: formatDuration(
      hasSpan
        ? Math.max(0, Math.round((until.getTime() - since.getTime()) / 1000))
        : null
    ),
  };
}
